// =============================================================================
// useClapDetection — passive mic listener that fires on a double-clap gesture
// =============================================================================
// Runs the mic through an AnalyserNode and watches the RMS volume for two
// sharp transients within a configurable window. Pure local math — never
// records, never transmits audio.
//
// Opt-in: enabled defaults to false and is controlled by the consumer
// (Settings → "Clap to call"). When disabled, the mic stream is fully torn
// down so nothing is monitored.
//
// When the in-app call starts the mic, the call SDK opens its own
// getUserMedia() against the same device. Browsers share mic access cleanly
// across getUserMedia calls — no conflict — but we still stop our stream on
// disable so the OS-level "tab is using your mic" indicator goes dark when
// neither the call nor clap detection is active.
// =============================================================================

import { useEffect, useRef } from 'react'

export interface UseClapDetectionOptions {
  enabled: boolean
  /** RMS threshold 0–1. 0.35 is a good middle. Lower = more sensitive. */
  sensitivity?: number
  /** Two claps must land within this window to count as a double-clap. */
  windowMs?: number
  /** Minimum gap between the two claps — rejects a single loud slap counted twice. */
  minGapMs?: number
}

export function useClapDetection(
  onDoubleClap: () => void,
  options: UseClapDetectionOptions,
): void {
  const { enabled, sensitivity = 0.35, windowMs = 500, minGapMs = 80 } = options

  // Hold the callback in a ref so we don't tear down the audio graph on every
  // render — only on enable/disable or tuning changes.
  const fireRef = useRef(onDoubleClap)
  fireRef.current = onDoubleClap

  useEffect(() => {
    if (!enabled) return

    let stream: MediaStream | null = null
    let audioCtx: AudioContext | null = null
    let rafHandle = 0
    let cancelled = false
    let lastClapTime = 0
    let inClap = false

    const VOLUME_THRESHOLD = Math.min(0.9, Math.max(0.1, sensitivity))
    const RESET_LEVEL = VOLUME_THRESHOLD * 0.5

    async function start(): Promise<void> {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        const AC: typeof AudioContext | undefined =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext
        if (!AC) return
        audioCtx = new AC()
        const source = audioCtx.createMediaStreamSource(stream)
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256
        source.connect(analyser)

        const data = new Uint8Array(analyser.frequencyBinCount)

        const tick = (): void => {
          if (cancelled) return
          analyser.getByteTimeDomainData(data as never)

          // Compute RMS in normalized [-1, 1] sample space. getByteTimeDomainData
          // returns 0-255 with 128 = silence.
          let sumSq = 0
          for (let i = 0; i < data.length; i += 1) {
            const v = ((data[i] as number) - 128) / 128
            sumSq += v * v
          }
          const rms = Math.sqrt(sumSq / data.length)

          if (rms > VOLUME_THRESHOLD && !inClap) {
            inClap = true
            const now = performance.now()
            const gap = now - lastClapTime
            if (gap > minGapMs && gap < windowMs) {
              // Second clap landed inside the window — fire!
              lastClapTime = 0
              fireRef.current()
            } else {
              lastClapTime = now
            }
          }
          if (rms < RESET_LEVEL) inClap = false

          rafHandle = window.requestAnimationFrame(tick)
        }
        tick()
      } catch {
        // Mic permission denied or no device — silently skip; the user can
        // still click the orb. We don't surface this as an error because the
        // toggle is opt-in and the user opted in knowing the risks.
      }
    }

    void start()

    return () => {
      cancelled = true
      if (rafHandle) window.cancelAnimationFrame(rafHandle)
      if (stream) stream.getTracks().forEach((t) => t.stop())
      if (audioCtx) void audioCtx.close().catch(() => undefined)
    }
  }, [enabled, sensitivity, windowMs, minGapMs])
}
